/**
 * ThreadView Component
 * 
 * Displays messages in a thread in Slack-style chronological order.
 * Includes an inline reply composer at the bottom.
 * Real-time updates are driven by the parent Notification component
 * via React Query invalidation — no per-instance WebSocket listeners.
 */
import React, { useState, useRef, useEffect } from 'react';
import { trpc } from '@/trpc';
import { Button, Input, Spin, Typography, Avatar, message, Tooltip } from 'antd';
import { SendOutlined, UserOutlined, CloseOutlined } from '@ant-design/icons';
import { formatTimestamp, parseHtmlMessage, getTypeConfig } from './index';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

interface ThreadViewProps {
  /** UUID of the thread to display */
  threadId: string;
  /** Callback when thread is closed */
  onClose?: () => void;
  /** Whether the thread view is expanded */
  isExpanded?: boolean;
}

/**
 * ThreadView displays all messages in a thread chronologically
 * with sender info and an inline reply composer
 */
export const ThreadView: React.FC<ThreadViewProps> = ({
  threadId,
  onClose,
  isExpanded = true
}) => {
  const [replyText, setReplyText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const threadContainerRef = useRef<HTMLDivElement>(null);

  // Fetch thread messages
  const threadQuery = trpc.inbox.getThread.useQuery(
    { threadId },
    {
      enabled: isExpanded,
      refetchOnMount: 'always', // Always fetch fresh data when ThreadView mounts (prevents stale cache)
      refetchOnWindowFocus: true // Enable refetch on window focus for dual-browser sync
    }
  );

  // Real-time updates are handled centrally by the parent Notification component:
  // - Parent listens to WebSocket events and calls utils.inbox.getThread.invalidate()
  // - React Query automatically refetches this query when invalidated
  // - This avoids O(n) WebSocket listeners for n expanded threads

  // Reply mutation
  const replyMutation = trpc.inbox.replyToThread.useMutation({
    onSuccess: () => {
      setReplyText('');
      setIsSending(false);
      threadQuery.refetch();
      message.success('Reply sent');
    },
    onError: (error) => {
      setIsSending(false);
      message.error(error.message || 'Failed to send reply');
    }
  });

  // Scroll to bottom and focus input when thread data loads or updates
  // dataUpdatedAt changes on every successful fetch, and useEffect always runs on mount
  useEffect(() => {
    if (!threadQuery.data?.messages) return undefined;

    const scrollToBottom = () => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
      if (threadContainerRef.current) {
        threadContainerRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
      if (textAreaRef.current) {
        textAreaRef.current.focus();
      }
    };

    // rAF ensures browser has completed layout before reading scrollHeight
    const rafId = requestAnimationFrame(scrollToBottom);
    return () => cancelAnimationFrame(rafId);
  }, [threadQuery.dataUpdatedAt]);

  const handleSendReply = async () => {
    const trimmedReply = replyText.trim();
    if (!trimmedReply) return;

    // Get the newest message in the thread to reply to
    // Backend returns DESC order, so first element is newest (before we reverse for display)
    const rawMessages = threadQuery.data?.messages || [];
    const newestMessage = rawMessages[0];
    
    if (!newestMessage) {
      message.error('No message to reply to');
      return;
    }

    setIsSending(true);
    replyMutation.mutate({
      parentId: newestMessage.id,
      message: trimmedReply
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Send on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  if (!isExpanded) return null;

  if (threadQuery.isLoading) {
    return (
      <div style={{ 
        padding: '24px', 
        textAlign: 'center',
        backgroundColor: '#fafafa',
        borderRadius: '8px',
        margin: '8px 0'
      }}>
        <Spin size="small" />
        <Text type="secondary" style={{ marginLeft: 8 }}>Loading thread...</Text>
      </div>
    );
  }

  if (threadQuery.error) {
    return (
      <div style={{ 
        padding: '16px', 
        textAlign: 'center',
        backgroundColor: '#fff2f0',
        borderRadius: '8px',
        margin: '8px 0',
        border: '1px solid #ffccc7'
      }}>
        <Text type="danger">Failed to load thread</Text>
      </div>
    );
  }

  // Reverse messages to show chronological order (oldest first, newest at bottom)
  // Backend returns DESC order (newest first), we want ASC for chat-style display
  const messages = [...(threadQuery.data?.messages || [])].reverse();

  return (
    <div
      ref={threadContainerRef}
      style={{
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        margin: '12px 0',
        border: '1px solid #e9ecef',
        overflow: 'hidden'
      }}>
      {/* Thread Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        backgroundColor: '#fff',
        borderBottom: '1px solid #e9ecef'
      }}>
        <Text strong style={{ color: '#495057' }}>
          Thread ({messages.length} {messages.length === 1 ? 'message' : 'messages'})
        </Text>
        {onClose && (
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={onClose}
            style={{ color: '#6c757d' }}
          />
        )}
      </div>

      {/* Messages List */}
      <div
        ref={messagesContainerRef}
        style={{
          maxHeight: '400px',
          overflowY: 'auto',
          padding: '8px 0'
        }}>
        {messages.map((msg, index) => {
          const senderName = msg.sender?.name || 
            (msg.metadata as Record<string, unknown>)?.senderName as string || 
            'Unknown';
          const isReply = index > 0;
          const typeConfig = getTypeConfig(msg.type || 'info');

          return (
            <div
              key={msg.id}
              style={{
                padding: '12px 16px',
                backgroundColor: isReply ? 'transparent' : '#fff',
                borderBottom: index < messages.length - 1 ? '1px solid #f0f0f0' : 'none'
              }}
            >
              {/* Message Header: Avatar + Sender + Timestamp + Read Status */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '8px',
                gap: '8px'
              }}>
                <Avatar 
                  size={28} 
                  icon={<UserOutlined />}
                  style={{ 
                    backgroundColor: typeConfig.borderColor,
                    color: typeConfig.icon.props.style.color
                  }}
                />
                <Text strong style={{ color: '#262626', fontSize: '13px' }}>
                  {senderName}
                </Text>
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {formatTimestamp(new Date(msg.createdAt))}
                </Text>
                {/* Show unread indicator - display unread recipient names */}
                {msg.unreadRecipientNames && msg.unreadRecipientNames.length > 0 && (
                  <Tooltip title={msg.unreadRecipientNames.join(', ')}>
                    <Text
                      style={{
                        fontSize: '11px',
                        color: '#8c8c8c'
                      }}
                    >
                      {msg.unreadRecipientNames.length} unread
                    </Text>
                  </Tooltip>
                )}
              </div>

              {/* Message Content */}
              <div style={{ paddingLeft: '36px' }}>
                <Paragraph
                  style={{
                    margin: 0,
                    color: '#495057',
                    fontSize: '13px',
                    lineHeight: '1.5'
                  }}
                >
                  {parseHtmlMessage(msg.message).map((part, partIndex) =>
                    part.isLineBreak ? (
                      <br key={partIndex} />
                    ) : (
                      <span
                        key={partIndex}
                        style={{
                          fontWeight: part.isBold ? 600 : 400,
                          color: part.isBold ? '#262626' : '#495057'
                        }}
                      >
                        {part.text}
                      </span>
                    )
                  )}
                </Paragraph>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply Composer */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: '#fff',
        borderTop: '1px solid #e9ecef'
      }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <TextArea
            ref={textAreaRef}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Write a reply... (Enter to send, Shift+Enter for new line)"
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={isSending}
            style={{
              borderRadius: '6px',
              resize: 'none'
            }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSendReply}
            loading={isSending}
            disabled={!replyText.trim()}
            style={{ 
              height: 'auto',
              minHeight: '32px'
            }}
          />
        </div>
        <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
          {threadQuery.data?.participantCount || 0}{' '}
          {threadQuery.data?.participantCount === 1 ? 'person' : 'people'} in this conversation
        </Text>
      </div>
    </div>
  );
};

export default ThreadView;
